import React from 'react';
import { ArrowRight, Briefcase, UserRound } from 'lucide-react';
import { LT } from '../../contexts/AppCatalogueContext';
import { COPY, tr } from './copy';
import { branches, cellFor, MGMT_RE, rowIndex, splitTracks, toneFor } from './model';

/* Overview · the whole model at a glance: the four stages as cards, then
 * the path from 1 to 9 with the optional manager track and the fork into
 * two tracks. Every level is a way into the Levels view. */
export default function Overview({ map, canon, locale, keywordRow, onOpenLevel }) {
  const { managerFrom, forkAt } = branches(canon);
  const count = map.levels.length;
  // Track names as the map writes them (in the reader's language).
  const mi = rowIndex(canon, MGMT_RE);
  const forkCell = forkAt && mi >= 0 ? cellFor(map.rows[mi], forkAt) : null;
  const tracks = (forkCell && splitTracks(forkCell.text)?.tracks.map(t => t.title)) || [];
  const col = (n) => map.levels.findIndex(l => l.n === n) + 1;

  return (
    <div className="mt-6 space-y-10">
      {/* Four stages */}
      <section aria-labelledby="cm-stages">
        <h2 id="cm-stages" className="text-[11px] uppercase tracking-[0.14em]" style={{ color: LT.green50 }}>{tr(COPY.stagesTitle, locale)}</h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {map.stages.map((s, si) => {
            const tone = toneFor(map, s.levels[0]);
            const keywords = keywordRow >= 0
              ? [...new Set(s.levels.map(n => cellFor(map.rows[keywordRow], n)?.text).filter(Boolean))]
              : [];
            return (
              <article key={si} className="flex flex-col min-w-0" style={{ background: LT.beigeLight, border: '1px solid rgba(43,65,56,0.12)' }}>
                <div className="px-5 pt-5 pb-4" style={{ background: tone.fill, color: tone.ink }}>
                  <div className="text-[11px] uppercase tracking-[0.14em] opacity-80">{s.question}</div>
                  <div className="font-display text-[2rem] leading-none mt-1">{s.key}</div>
                  <div className="text-[14px] mt-2 leading-snug">{s.title}</div>
                </div>
                <div className="px-5 py-4 flex-1 flex flex-col gap-3">
                  {s.summary && <p className="text-[13.5px] leading-relaxed" style={{ color: '#4d6157' }}>{s.summary}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {s.levels.map(n => {
                      const l = map.levels.find(x => x.n === n);
                      return (
                        <button key={n} type="button" onClick={() => onOpenLevel(n)}
                                className="text-[12.5px] px-2.5 py-1 rounded-full transition hover:opacity-80"
                                style={{ border: '1px solid rgba(43,65,56,0.2)', color: LT.greenDeep }}>
                          <span className="font-num">{n}</span> · {l?.name}
                        </button>
                      );
                    })}
                  </div>
                  {keywords.map((k, i) => {
                    const [head, ...rest] = k.split('\n').map(x => x.trim()).filter(Boolean);
                    return (
                      <div key={i} className="pt-3 border-t" style={{ borderColor: 'rgba(43,65,56,0.12)' }}>
                        <div className="font-display italic text-[15px]" style={{ color: LT.greenDeep }}>{(head || '').replace(/^✦\s*/, '')}</div>
                        {rest[0] && <div className="text-[12.5px]" style={{ color: LT.green50 }}>{rest[0]}</div>}
                      </div>
                    );
                  })}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* The path · one line of nine levels, the optional manager track and the fork */}
      <section aria-labelledby="cm-journey">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h2 id="cm-journey" className="text-[11px] uppercase tracking-[0.14em]" style={{ color: LT.green50 }}>{tr(COPY.journeyTitle, locale)}</h2>
          <span className="text-[12.5px]" style={{ color: LT.green50 }}>{tr(COPY.journeyNote, locale)}</span>
        </div>
        <div className="mt-3 overflow-x-auto pb-1">
          <div className="min-w-[640px] grid gap-x-1 gap-y-2 items-center" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
            {/* main line */}
            <div className="h-px" style={{ gridColumn: `1 / span ${count}`, gridRow: 1, background: 'rgba(43,65,56,0.25)', alignSelf: 'center' }} />
            {map.levels.map(l => {
              const tone = toneFor(map, l.n);
              return (
                <button key={l.n} type="button" onClick={() => onOpenLevel(l.n)}
                        className="justify-self-center flex flex-col items-center gap-1 group"
                        style={{ gridColumn: col(l.n), gridRow: 1 }}>
                  <span className="h-11 w-11 rounded-full grid place-items-center font-num text-[16px] transition group-hover:-translate-y-0.5"
                        style={{ background: tone.fill, color: tone.ink, boxShadow: '0 0 0 4px #FBFBFA' }}>{l.n}</span>
                </button>
              );
            })}
            {map.levels.map(l => (
              <div key={`n${l.n}`} className="text-center text-[12px] leading-tight px-0.5" style={{ gridColumn: col(l.n), gridRow: 2, color: LT.greenDeep }}>
                {l.name}<div className="font-num text-[10.5px]" style={{ color: LT.green50 }}>{l.years}</div>
              </div>
            ))}

            {managerFrom && (
              <div className="flex items-center gap-2 text-[12px] px-3 py-1.5 mt-2"
                   style={{ gridColumn: `${col(managerFrom)} / span ${Math.max(1, (forkAt ? col(forkAt) : count + 1) - col(managerFrom))}`, gridRow: 3,
                            border: `1px dashed ${LT.greenMid}`, color: '#4d6157' }}>
                <UserRound className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{tr(COPY.managerOpt, locale)}</span>
              </div>
            )}
            {forkAt && (
              <>
                <div className="flex items-center gap-2 text-[12.5px] px-3 py-2 mt-2"
                     style={{ gridColumn: `${col(forkAt)} / span ${count - col(forkAt) + 1}`, gridRow: 3, background: STAGE_FORK_BG, color: LT.greenDeep }}>
                  <Briefcase className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{tracks[0] || 'Expert'}</span>
                </div>
                <div className="flex items-center gap-2 text-[12.5px] px-3 py-2"
                     style={{ gridColumn: `${col(forkAt)} / span ${count - col(forkAt) + 1}`, gridRow: 4, background: STAGE_FORK_BG, color: LT.greenDeep }}>
                  <UserRound className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{tracks[1] || 'Manager'}</span>
                </div>
                <div className="text-[11.5px] text-center" style={{ gridColumn: `${col(forkAt)} / span ${count - col(forkAt) + 1}`, gridRow: 5, color: LT.green50 }}>
                  {tr(COPY.dualTrack, locale)}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <button type="button" onClick={() => onOpenLevel(map.levels[0]?.n || 1)}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full text-[15px] transition hover:opacity-90"
              style={{ background: LT.cta, color: '#fff' }}>
        {tr(COPY.explore, locale)} <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

const STAGE_FORK_BG = '#E4EBE6';
